import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckSquare, MapPin, User, Check, Loader2, X, Map as MapIcon,
  Calendar, AlertOctagon, Plus, Link2, Camera,
} from 'lucide-react';
import C from '../../constants/colors';
import * as actionsApi from '../../api/actions';
import * as usersApi from '../../api/users';

const STATUS_META = {
  OPEN:        { label: 'Open',        color: C.red },
  IN_PROGRESS: { label: 'In Progress', color: C.amber },
  COMPLETED:   { label: 'Completed',   color: C.green },
};

function formatAge(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Returns { label, color } for a due date — overdue / soon / future
function dueDateMeta(dueDate, status) {
  if (!dueDate || status === 'COMPLETED') return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0)  return { label: `Overdue ${-diffDays}d`, color: C.red };
  if (diffDays === 0) return { label: 'Due today',            color: C.amber };
  if (diffDays <= 3) return { label: `Due in ${diffDays}d`,   color: C.amber };
  return { label: `Due in ${diffDays}d`, color: C.textSecondary };
}

export default function ActionsView({
  siteId, site, zones, actions, incidents, currentUserId, onRefresh, onCreateClick,
  viewTitle = 'Actions', defaultFilterStatus = 'OPEN', initialSelectedId = null,
}) {
  const [filterStatus, setFilterStatus] = useState(initialSelectedId ? 'ALL' : defaultFilterStatus);
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);
  const [photoModal, setPhotoModal] = useState(false);
  const [mapModal, setMapModal] = useState(false);

  // Users (for assignee picker) — loaded on mount
  const [users, setUsers] = useState([]);
  useEffect(() => {
    usersApi.listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  // Mutation state
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [completionDraft, setCompletionDraft] = useState('');
  const [completionPhotoDataUrl, setCompletionPhotoDataUrl] = useState(null);
  const [completeMode, setCompleteMode] = useState(false);
  // Notes
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState('');

  const filtered = useMemo(() => {
    return (actions || []).filter(a => {
      if (filterStatus === 'MY_OPEN') {
        if (a.status === 'COMPLETED') return false;
        if (a.assignedUserId !== currentUserId) return false;
      } else if (filterStatus !== 'ALL' && a.status !== filterStatus) return false;
      return true;
    });
  }, [actions, filterStatus, currentUserId]);

  const selected = filtered.find(a => a.id === selectedId) || (actions || []).find(a => a.id === selectedId) || null;

  // Reset draft state when selection changes
  useEffect(() => {
    setCompletionDraft(selected?.completionNote || '');
    setCompletionPhotoDataUrl(null);
    setCompleteMode(false);
    setErrorMsg('');
    setNoteDraft('');
    setNoteError('');
  }, [selectedId]);

  // ── Mutations ─────────────────────────────────────────────────
  async function handleAssign(userId) {
    if (!selected) return;
    setBusy(true); setErrorMsg('');
    try {
      await actionsApi.assign(selected.id, userId || null);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to assign');
    } finally { setBusy(false); }
  }

  async function handleSetStatus(newStatus) {
    if (!selected) return;
    if (newStatus === 'COMPLETED') {
      setCompleteMode(true);
      return;
    }
    setBusy(true); setErrorMsg('');
    try {
      await actionsApi.setStatus(selected.id, newStatus);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to update status');
    } finally { setBusy(false); }
  }

  async function handleConfirmComplete() {
    if (!selected || !completionDraft.trim()) return;
    setBusy(true); setErrorMsg('');
    try {
      await actionsApi.setStatus(
        selected.id,
        'COMPLETED',
        completionDraft.trim(),
        completionPhotoDataUrl || undefined,
      );
      setCompleteMode(false);
      setCompletionPhotoDataUrl(null);
      await onRefresh?.();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to complete action');
    } finally { setBusy(false); }
  }

  async function handleAddNote() {
    if (!selected || !noteDraft.trim()) return;
    setNoteBusy(true); setNoteError('');
    try {
      await actionsApi.addNote(selected.id, noteDraft.trim());
      setNoteDraft('');
      await onRefresh?.();
    } catch (err) {
      setNoteError(err?.response?.data?.error || 'Failed to add note');
    } finally { setNoteBusy(false); }
  }

  // ── Styles ────────────────────────────────────────────────────
  const pill = (active, color) => ({
    padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${active ? color : C.border}`,
    background: active ? color + '22' : 'transparent',
    color: active ? color : C.textSecondary,
  });

  const counts = useMemo(() => {
    const myOpen = (actions || []).filter(a => a.assignedUserId === currentUserId && a.status !== 'COMPLETED').length;
    return {
      ALL:         (actions || []).length,
      OPEN:        (actions || []).filter(a => a.status === 'OPEN').length,
      IN_PROGRESS: (actions || []).filter(a => a.status === 'IN_PROGRESS').length,
      COMPLETED:   (actions || []).filter(a => a.status === 'COMPLETED').length,
      MY_OPEN:     myOpen,
    };
  }, [actions, currentUserId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <CheckSquare size={20} color={C.blue} />
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>{viewTitle}</h2>
          {site && <span style={{ fontSize: '13px', color: C.textSecondary }}>· {site.name}</span>}
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>{filtered.length} of {(actions || []).length}</span>
          {onCreateClick && (
            <button onClick={onCreateClick}
              style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Plus size={13} />
              New Action
            </button>
          )}
        </div>

        {/* Status pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {[
            ['MY_OPEN',     'My Open',     C.blue,   counts.MY_OPEN],
            ['ALL',         'All',         C.textSecondary, counts.ALL],
            ['OPEN',        'Open',        STATUS_META.OPEN.color,        counts.OPEN],
            ['IN_PROGRESS', 'In Progress', STATUS_META.IN_PROGRESS.color, counts.IN_PROGRESS],
            ['COMPLETED',   'Completed',   STATUS_META.COMPLETED.color,   counts.COMPLETED],
          ].map(([key, label, color, count]) => (
            <button key={key} onClick={() => setFilterStatus(key)} style={pill(filterStatus === key, color)}>
              {label} {count > 0 && <span style={{ opacity: 0.7, marginLeft: '4px' }}>· {count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Body: master/detail */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* List */}
        <div style={{ width: '380px', borderRight: `1px solid ${C.border}`, overflowY: 'auto', flexShrink: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 20px', color: C.textMuted, fontSize: '13px', textAlign: 'center' }}>
              No actions match the current filters.
            </div>
          ) : filtered.map(a => {
            const stm = STATUS_META[a.status] || STATUS_META.OPEN;
            const due = dueDateMeta(a.dueDate, a.status);
            const isSelected = selectedId === a.id;
            return (
              <div key={a.id} onClick={() => setSelectedId(a.id)}
                style={{ display: 'flex', alignItems: 'stretch', cursor: 'pointer',
                  borderLeft: `3px solid ${due?.color === C.red ? C.red : stm.color}`,
                  background: isSelected ? C.surfaceElevated : 'transparent',
                  borderBottom: `1px solid ${C.border}` }}>
                <div style={{ padding: '12px 14px', flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    {a.incidentId && <Link2 size={11} color={C.textMuted} />}
                    {due && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: due.color, padding: '2px 6px', borderRadius: '4px', background: due.color + '22' }}>{due.label}</span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 700, color: stm.color, textTransform: 'uppercase' }}>{stm.label}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.description}
                  </div>
                  <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', gap: '10px' }}>
                    {a.siteName && <span>{a.siteName}</span>}
                    <span style={{ marginLeft: 'auto' }}>{formatAge(a.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {!selected ? (
            <div style={{ color: C.textMuted, fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
              Select an action to view details.
            </div>
          ) : (
            <ActionDetail
              action={selected}
              linkedIncident={(incidents || []).find(i => i.id === selected.incidentId) || null}
              site={site}
              zones={zones}
              users={users}
              busy={busy}
              errorMsg={errorMsg}
              completionDraft={completionDraft}
              setCompletionDraft={setCompletionDraft}
              completionPhotoDataUrl={completionPhotoDataUrl}
              setCompletionPhotoDataUrl={setCompletionPhotoDataUrl}
              completeMode={completeMode}
              setCompleteMode={setCompleteMode}
              onAssign={handleAssign}
              onSetStatus={handleSetStatus}
              onConfirmComplete={handleConfirmComplete}
              onOpenPhoto={() => setPhotoModal(true)}
              onOpenMap={() => setMapModal(true)}
              noteDraft={noteDraft}
              setNoteDraft={setNoteDraft}
              noteBusy={noteBusy}
              noteError={noteError}
              onAddNote={handleAddNote}
            />
          )}
        </div>
      </div>

      {/* Photo lightbox */}
      {photoModal && selected?.completionPhotoUrl && (
        <div onClick={() => setPhotoModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', cursor: 'zoom-out' }}>
          <img src={selected.completionPhotoUrl} alt="completion" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '12px' }} />
        </div>
      )}

      {/* Map modal — reuses same Leaflet pattern as incidents */}
      {mapModal && selected && (
        <ActionMapModal
          action={selected}
          site={site}
          zones={zones}
          onClose={() => setMapModal(false)}
        />
      )}
    </div>
  );
}

// Resize an image File to keep payload small
async function resizeImage(file, maxDim = 1200, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// ── Detail panel ──────────────────────────────────────────────────
function ActionDetail({
  action, linkedIncident, site, zones, users, busy, errorMsg,
  completionDraft, setCompletionDraft, completionPhotoDataUrl, setCompletionPhotoDataUrl,
  completeMode, setCompleteMode,
  onAssign, onSetStatus, onConfirmComplete, onOpenPhoto, onOpenMap,
  noteDraft, setNoteDraft, noteBusy, noteError, onAddNote,
}) {
  const stm = STATUS_META[action.status];
  const due = dueDateMeta(action.dueDate, action.status);
  const photoRef = useRef();

  const incidentZone = (zones || []).find(z => z.id === action.zoneId);
  const hasMapCoords =
    (action.gpsLat != null && action.gpsLng != null) ||
    (incidentZone?.lat != null && incidentZone?.lng != null) ||
    (site?.lat != null && site?.lng != null);

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file);
      setCompletionPhotoDataUrl(dataUrl);
    } catch {/* ignore */}
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <CheckSquare size={20} color={C.blue} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary }}>Action</div>
        {due && (
          <div style={{ fontSize: '11px', fontWeight: 700, color: due.color, padding: '3px 8px', borderRadius: '6px', background: due.color + '22' }}>{due.label}</div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {hasMapCoords && (
            <button onClick={onOpenMap}
              style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapIcon size={13} />
              MAP
            </button>
          )}
          <div style={{ fontSize: '11px', fontWeight: 700, color: stm.color, padding: '3px 8px', borderRadius: '6px', background: stm.color + '22', textTransform: 'uppercase' }}>{stm.label}</div>
        </div>
      </div>

      {/* Context grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <InfoRow icon={MapPin} label="Site" value={action.siteName || '—'} />
        <InfoRow icon={Calendar} label="Due Date" value={action.dueDate ? new Date(action.dueDate).toLocaleDateString() : 'No due date'} />
      </div>

      {/* Linked incident */}
      {action.incidentId && (
        <div style={{ marginBottom: '16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Link2 size={11} />
            Linked Incident
          </div>
          <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500 }}>
            {linkedIncident ? (
              <>
                <span style={{ color: C.red, fontWeight: 700 }}>{linkedIncident.incidentType?.replace(/_/g, ' ')}</span>
                {' · '}{linkedIncident.description?.slice(0, 80)}{linkedIncident.description?.length > 80 ? '…' : ''}
              </>
            ) : (
              <span style={{ color: C.textMuted, fontStyle: 'italic' }}>{action.incidentId}</span>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Description</div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', color: C.textPrimary, fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {action.description}
        </div>
      </div>

      {/* Completion photo (if any) */}
      {action.completionPhotoUrl && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Completion Photo</div>
          <img src={action.completionPhotoUrl} alt="completion" onClick={onOpenPhoto}
            style={{ maxWidth: '320px', maxHeight: '240px', borderRadius: '10px', cursor: 'zoom-in', border: `1px solid ${C.border}`, display: 'block' }} />
        </div>
      )}

      {/* Assignee picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Assignee</div>
        <select
          value={action.assignedUserId || ''}
          disabled={busy || action.status === 'COMPLETED'}
          onChange={e => onAssign(e.target.value || null)}
          style={{ width: '100%', maxWidth: '320px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
          <option value="">— Unassigned —</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
        </select>
      </div>

      {/* Status actions */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Status</div>
        {completeMode ? (
          <div>
            <textarea
              value={completionDraft}
              onChange={e => setCompletionDraft(e.target.value)}
              placeholder="What was done? (required to complete)…"
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '10px' }}
            />

            {/* Optional photo */}
            <div style={{ marginBottom: '10px' }}>
              {completionPhotoDataUrl ? (
                <div style={{ position: 'relative', display: 'inline-block', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
                  <img src={completionPhotoDataUrl} alt="evidence" style={{ maxWidth: '240px', maxHeight: '180px', display: 'block' }} />
                  <button onClick={() => setCompletionPhotoDataUrl(null)} type="button"
                    style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', border: `1px dashed ${C.border}`, background: C.surface, color: C.textSecondary, fontSize: '12px', cursor: 'pointer' }}>
                  <Camera size={14} />
                  Add evidence photo (optional)
                  <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoChange} />
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={onConfirmComplete} disabled={!completionDraft.trim() || busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: completionDraft.trim() ? C.green : C.surfaceElevated, color: completionDraft.trim() ? '#fff' : C.textMuted, fontSize: '13px', fontWeight: 600, cursor: completionDraft.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {busy ? <Loader2 size={14} className="spinning" /> : <Check size={14} />}
                Confirm Complete
              </button>
              <button onClick={() => { setCompleteMode(false); setCompletionPhotoDataUrl(null); }} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {action.status === 'OPEN' && action.assignedUserId && (
              <button onClick={() => onSetStatus('IN_PROGRESS')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${STATUS_META.IN_PROGRESS.color}`, background: STATUS_META.IN_PROGRESS.color + '22', color: STATUS_META.IN_PROGRESS.color, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Mark In Progress
              </button>
            )}
            {action.status !== 'COMPLETED' && (
              <button onClick={() => onSetStatus('COMPLETED')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.green}`, background: C.green + '22', color: C.green, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Complete…
              </button>
            )}
            {action.status === 'COMPLETED' && (
              <button onClick={() => onSetStatus('IN_PROGRESS')} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${STATUS_META.IN_PROGRESS.color}`, background: STATUS_META.IN_PROGRESS.color + '22', color: STATUS_META.IN_PROGRESS.color, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Reopen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Completion note (when COMPLETED) */}
      {action.status === 'COMPLETED' && action.completionNote && !completeMode && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Completion Note</div>
          <div style={{ background: C.green + '11', border: `1px solid ${C.green}33`, borderRadius: '8px', padding: '12px', color: C.textPrimary, fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {action.completionNote}
          </div>
        </div>
      )}

      {/* Notes thread */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
          {(action.notes?.length || 0) > 0 && (
            <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600 }}>· {action.notes.length}</span>
          )}
        </div>

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
          <textarea
            value={noteDraft || ''}
            onChange={e => setNoteDraft(e.target.value)}
            placeholder={action.status === 'COMPLETED' ? 'This action is completed.' : 'Add a working note…'}
            rows={2}
            disabled={action.status === 'COMPLETED' || noteBusy}
            style={{ width: '100%', boxSizing: 'border-box', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '8px', opacity: action.status === 'COMPLETED' ? 0.5 : 1 }}
          />
          {noteError && <div style={{ fontSize: '12px', color: C.red, marginBottom: '6px' }}>{noteError}</div>}
          <button
            onClick={onAddNote}
            disabled={!noteDraft?.trim() || noteBusy || action.status === 'COMPLETED'}
            style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', background: noteDraft?.trim() && !noteBusy && action.status !== 'COMPLETED' ? C.blue : C.surfaceElevated, color: noteDraft?.trim() && !noteBusy && action.status !== 'COMPLETED' ? '#fff' : C.textMuted, fontSize: '12px', fontWeight: 700, cursor: noteDraft?.trim() && !noteBusy && action.status !== 'COMPLETED' ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
            {noteBusy ? 'Adding…' : 'Add Note'}
          </button>
        </div>

        {(action.notes?.length || 0) === 0 ? (
          <div style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic', padding: '8px 4px' }}>No notes yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[...action.notes].reverse().map(n => (
              <div key={n.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: C.textPrimary }}>{n.userName}</span>
                  <span style={{ fontSize: '11px', color: C.textMuted }}>· {new Date(n.createdAt).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: '13px', color: C.textPrimary, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.note}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Timeline</div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px', fontSize: '12px', color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.7 }}>
          <div>Created    · {new Date(action.createdAt).toLocaleString()}</div>
          {action.assignedAt    && <div>Assigned   · {new Date(action.assignedAt).toLocaleString()}</div>}
          {action.inProgressAt  && <div>In Progress· {new Date(action.inProgressAt).toLocaleString()}</div>}
          {action.completedAt   && <div>Completed  · {new Date(action.completedAt).toLocaleString()}</div>}
          {action.gpsLat != null && action.gpsLng != null && (
            <div>GPS        · {action.gpsLat.toFixed(5)}, {action.gpsLng.toFixed(5)}</div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: C.red }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px' }}>
      <div style={{ fontSize: '10px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Icon size={11} />
        {label}
      </div>
      <div style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// ── Map modal (mirror of IncidentMapModal) ────────────────────────
function ActionMapModal({ action, site, zones, onClose }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const incidentZone = (zones || []).find(z => z.id === action.zoneId);
  const anchor = (() => {
    if (action.gpsLat != null && action.gpsLng != null) {
      return { lat: action.gpsLat, lng: action.gpsLng, source: 'action' };
    }
    if (incidentZone?.lat != null && incidentZone?.lng != null) {
      return { lat: incidentZone.lat, lng: incidentZone.lng, source: 'zone' };
    }
    if (site?.lat != null && site?.lng != null) {
      return { lat: site.lat, lng: site.lng, source: 'site' };
    }
    return null;
  })();

  useEffect(() => {
    if (!anchor) return;
    function init() {
      if (!mapRef.current || mapInstance.current) return;
      const L = window.L;
      const map = L.map(mapRef.current).setView([anchor.lat, anchor.lng], 18);
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_API_KEY || ''}`, {
        attribution: '© CartoDB', maxZoom: 19,
      }).addTo(map);
      mapInstance.current = map;

      if (site?.lat != null && site?.lng != null && anchor.source !== 'site') {
        L.circleMarker([site.lat, site.lng], {
          radius: 7, color: '#94a3b8', weight: 2, fillOpacity: 0.4, fillColor: '#94a3b8',
        }).addTo(map).bindTooltip(`Site: ${site.name}`);
      }

      const color = action.status === 'COMPLETED' ? '#22c55e' : '#3b82f6';
      const html = `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 4px ${color}55;"></div>`;
      const icon = L.divIcon({ html, className: '', iconSize: [18, 18], iconAnchor: [9, 9] });
      L.marker([anchor.lat, anchor.lng], { icon })
        .addTo(map)
        .bindPopup(`<b>Action</b><br>${action.status}<br>${(action.description || '').slice(0, 80)}`)
        .openPopup();
    }

    if (window.L) { init(); }
    else if (!document.getElementById('leaflet-script')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.id = 'leaflet-script';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = init;
      document.head.appendChild(script);
    } else {
      const t = setInterval(() => { if (window.L) { clearInterval(t); init(); } }, 80);
      return () => clearInterval(t);
    }

    return () => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
    };
  }, [action?.id]);

  if (!anchor) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '24px', maxWidth: '400px', textAlign: 'center', color: C.textSecondary, fontSize: '14px' }}>
          No GPS location is available for this action.
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', width: '100%', maxWidth: '900px', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MapIcon size={16} color={C.blue} />
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>Action Location</div>
          <div style={{ fontSize: '11px', color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
            {anchor.lat.toFixed(5)}, {anchor.lng.toFixed(5)}
            <span style={{ marginLeft: '8px', color: anchor.source === 'action' ? C.green : C.amber }}>
              ({anchor.source === 'action' ? 'GPS' : `falling back to ${anchor.source}`})
            </span>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>
        <div ref={mapRef} style={{ flex: 1, minHeight: 0 }} />
      </div>
    </div>
  );
}
